/**
 * Utility function to get a user-friendly error message
 * @param error - The error object from a catch block
 * @param fallbackMessage - The fallback message if error is not an Error instance
 * @returns The error message to display to the user
 */
export function getErrorMessage(
  error: unknown,
  fallbackMessage: string
): string {
  if (error instanceof Error) {
    return error.message;
  }
  return fallbackMessage;
}

// detail covers FastAPI-shaped errors from non-Fastify upstreams
export function resolveErrorMessage(data: unknown, fallback: string): string {
  const record = data as Record<string, unknown> | undefined;

  if (record?.error) {
    if (typeof record.error === 'string') return record.error;
    const errorObj = record.error as Record<string, unknown>;
    if (typeof errorObj?.message === 'string') return errorObj.message;
    return JSON.stringify(record.error);
  }

  if (record?.message) {
    return typeof record.message === 'string'
      ? record.message
      : JSON.stringify(record.message);
  }

  if (record?.detail) {
    if (typeof record.detail === 'string') return record.detail;
    if (Array.isArray(record.detail)) {
      return record.detail.map((d: any) => d?.msg || d?.message).join(', ');
    }
    return JSON.stringify(record.detail);
  }

  return fallback;
}

// Assumes error.response exists — callers branch on that before calling
export function getApiErrorMessage(error: any): string {
  return resolveErrorMessage(
    error.response?.data,
    `HTTP ${error.response.status}: ${error.response.statusText}`
  );
}
